import type * as Blockly from 'blockly/core';
import { BOARD_CONFIG } from '@/(routers)/boards/editor/block/blockConfig';
import { indent, registerSetupCode, resolveBoard } from '@/(routers)/boards/editor/block/generators/arduino/_gen';
import {
    irRecvDefineCodes,
    markIrEsp8266Used,
    markIrMatchRawBlueUsed,
    markIrMatchRawUsed,
    markIrRemoteUsed,
} from '@/(routers)/boards/editor/block/generators/arduino/blocks/action/helper/ir_helper';

type Position = keyof (typeof BOARD_CONFIG)[keyof typeof BOARD_CONFIG]['pins'];

const PRESET_RAW_DATA_ORANGE: Record<string, number[]> = {
    '1': [26, 7, 26, 8, 8, 25, 26, 7, 9, 25, 8, 25, 9, 24, 8, 25, 9, 25, 25, 7, 9, 25, 9],
    '2': [26, 8, 25, 8, 9, 25, 24, 8, 9, 25, 8, 25, 25, 8, 9, 25, 8, 25, 8, 25, 9, 25, 8],
    '3': [25, 8, 25, 8, 9, 25, 25, 8, 8, 25, 25, 8, 9, 25, 8, 25, 8, 25, 9, 25, 25, 7, 26],
    '4': [25, 8, 26, 8, 8, 25, 25, 8, 9, 25, 8, 25, 8, 25, 25, 8, 9, 25, 8, 25, 8, 25, 9],
    '5': [25, 8, 26, 7, 9, 25, 25, 8, 9, 24, 9, 24, 9, 25, 9, 24, 9, 24, 9, 24, 9, 25, 25],
};

const PRESET_RAW_DATA_BLUE: Record<string, number[]> = {
    '1': [
        650, 175, 650, 200, 200, 625, 650, 175, 225, 625, 200, 625, 225, 600, 200, 625, 225, 625, 625, 175, 225, 625,
        225,
    ],
    '2': [
        650, 200, 625, 200, 225, 625, 600, 200, 225, 625, 200, 625, 625, 200, 225, 625, 200, 625, 200, 625, 225, 625,
        200,
    ],
    '3': [
        625, 200, 625, 200, 225, 625, 625, 200, 200, 625, 625, 200, 225, 625, 200, 625, 200, 625, 225, 625, 625, 175,
        650,
    ],
    '4': [
        625, 200, 650, 200, 200, 625, 625, 200, 225, 625, 200, 625, 200, 625, 625, 200, 225, 625, 200, 625, 200, 625,
        225,
    ],
    '5': [
        625, 200, 650, 175, 225, 625, 625, 200, 225, 600, 225, 600, 225, 625, 225, 600, 225, 600, 225, 600, 225, 625,
        625,
    ],
};

export function register(gen: any): void {
    gen.forBlock['ir_receiver'] = function (block: Blockly.Block): string {
        const position = block.getFieldValue('POSITION') as Position;
        const irCode = block.getFieldValue('IR_CODE') as string;
        const board = resolveBoard(block);
        const pin = BOARD_CONFIG[board].pins[position].second;
        const doCode = indent(gen.statementToCode(block, 'DO') as string, 1);

        if (board === 'blue') {
            markIrEsp8266Used();
            markIrMatchRawBlueUsed();
            irRecvDefineCodes.set(String(pin), `IRrecv irrecv_${pin}(${pin});\ndecode_results irResults_${pin};\n`);
            registerSetupCode(`irrecv_${pin}`, `  irrecv_${pin}.enableIRIn();\n`);

            const rawData = PRESET_RAW_DATA_BLUE[irCode];
            const refName = `presetRef_${irCode}`;
            const rawTick = rawData.join(',');
            const refLen = rawData.length;

            return (
                `if (irrecv_${pin}.decode(&irResults_${pin})) {\n` +
                `  uint16_t ${refName}[] = {${rawTick}};\n` +
                `  uint16_t *recv = const_cast<uint16_t *>(irResults_${pin}.rawbuf) + 1;\n` +
                `  int recvLen = (int)irResults_${pin}.rawlen - 1;\n` +
                `  if (matchRawBlue(recv, recvLen, ${refName}, ${refLen})) {\n` +
                doCode +
                `  }\n` +
                `  irrecv_${pin}.resume();\n` +
                `}\n`
            );
        } else {
            markIrRemoteUsed();
            markIrMatchRawUsed();
            irRecvDefineCodes.set(String(pin), `IRrecv irrecv_${pin}(${pin});\n`);
            registerSetupCode(`irrecv_${pin}`, `  irrecv_${pin}.enableIRIn();\n`);

            const rawData = PRESET_RAW_DATA_ORANGE[irCode];
            const refName = `presetRef_${irCode}`;
            const rawTick = rawData.join(',');
            const refLen = rawData.length;

            return (
                `if (irrecv_${pin}.decode()) {\n` +
                `  uint8_t ${refName}[] = {${rawTick}};\n` +
                `  uint8_t *recv = irrecv_${pin}.decodedIRData.rawDataPtr->rawbuf + 1;\n` +
                `  int recvLen = (int)irrecv_${pin}.decodedIRData.rawDataPtr->rawlen - 1;\n` +
                `  if (matchRaw(recv, recvLen, ${refName}, ${refLen})) {\n` +
                doCode +
                `  }\n` +
                `  irrecv_${pin}.resume();\n` +
                `}\n`
            );
        }
    };
}
